import { Result } from '@youhelp-ru/core';
import { ClientRegistrationState } from '@common/model/client/registration';
import { ClientProfileViewModel } from '@common/model/client';
import { useCallback } from 'react';
import { coreCurrentClientApi } from '@cp/entities/client';
import { ClientSelection } from 'src/entities/client';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { ClientType } from '@common/model/client/clientType';
import { useClientWebResolver } from '@cp/entities/clientWebRoutes';
import { useRouter } from '@cp/shared/router';
import { useInjection } from '@cp/shared/di';
import { useModalHandler, useModalPromise } from '@cp/shared/modal';
import { useDispatch } from 'react-redux';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { useTrackEvent } from 'src/shared/tracking';
import { useIntentionService } from 'src/shared/intent';
import { stepToLayout } from '../ui/stepToComponent';
import { GET_CLIENT_SELECTIONS } from '../di';
import { ClientSelectCareer, REGISTRATION_MODAL, registrationClientTypeSelect, registrationFinish, registrationStart } from '../lib';
import { slice } from '../api';
import { useRegistrationService } from './useRegistrationService';

export const useRegistrationFeature = (): ((
  s: string,
  state: Partial<ClientRegistrationState>,
) => Promise<Result<ClientProfileViewModel, Error>>) => {
  const service = useRegistrationService();
  const openStepModal = useModalPromise(REGISTRATION_MODAL);
  const router = useRouter();
  const resolver = useClientWebResolver();
  const getClientSelections = useInjection(GET_CLIENT_SELECTIONS);
  const [getProfile] = coreCurrentClientApi.endpoints.getProfile.useLazyQuery();
  const trackEvent = useTrackEvent();
  const dispatch = useDispatch();
  const intentionService = useIntentionService();

  return useCallback(
    async (sessionId: string, initialState: Partial<ClientRegistrationState>) => {
      const selections: ClientSelection = await getClientSelections();
      const state = {
        ...selections,
        mainCareerId: selections.careerId || initialState.mainCareerId,
        ...initialState,
      };
      trackEvent(registrationStart({ type: state.clientType }));
      if (state.clientType === ClientType.Specialist) {
        await router.navigate(
          resolver.resolve({
            page: ClientWebPage.SignupProvider,
            queryParams: { careerId: state.mainCareerId, sessionId },
          }),
        );
      }
      await service.init(sessionId, state);
      let step = service.getStep();
      dispatch(slice.actions.setStep(step.value));

      while (!step.done && step.value.step !== null) {
        const stepState = await openStepModal(
          {
            step: step.value.step,
            onValidate: (value) => service.completeStep(value),
          },
          undefined,
          stepToLayout[step.value.step.name],
        );

        if (stepState.clientType) {
          trackEvent(registrationClientTypeSelect({ type: stepState.clientType }));
        } else if (stepState.mainCareerId) {
          trackEvent(ClientSelectCareer(stepState.mainCareerId));
        }

        step = service.getStep();
      }

      const profile = await Result.fromAsync(() => getProfile().unwrap());

      const hasIntent = intentionService.has();

      let redirectUrl = resolver.resolve({ page: ClientWebPage.Profile });
      return profile
        .mapError((err) => (err instanceof Error ? err : new Error('Can not get profile after registration')))
        .tap((p) => trackEvent(registrationFinish(p)))
        .tap(({ type, mainCareerId, city }) => {
          if (hasIntent && type && mainCareerId) {
            redirectUrl = resolver.resolve({
              page: ClientWebPage.Catalog,
              cityAlias: city?.alias,
              pageProps: {
                catalogType: clientTypeToSuitableCatalogItemType(type),
                careerId: mainCareerId,
              },
            });
          } else if (type === ClientType.Employer && mainCareerId) {
            redirectUrl = resolver.resolve({
              page: ClientWebPage.ProfileVacancies,
              queryParams: { openCreateVacancy: mainCareerId },
            });
          }
        })
        .tap(() => {
          // redirect client after registration
          router.navigate(redirectUrl);
        });
    },
    [service, openStepModal, getProfile, router, resolver, getClientSelections, dispatch, trackEvent, intentionService],
  );
};
